import client from './client'
import { WeeklyViewData, WorkAssignment, TimeEntry, Absence, AbsenceAttachment, AbsenceTypeConfig, HalfDay, HolidayInfo, AbsenceNotifyRecipient, PlanningAbsenceUser } from '../types'

export async function getWeeklyView(year: number, week: number): Promise<WeeklyViewData> {
  const response = await client.get('/attendance/weekly', { params: { year, week } })
  return response.data.data
}

export interface PermanentOrder {
  id: string
  number: string
  company_name: string
  description: string | null
}

export async function getPermanentOrders(): Promise<PermanentOrder[]> {
  const response = await client.get('/attendance/permanent-orders')
  return response.data.data
}

export interface PayrollDayEntry {
  id: string
  order_id: string
  order_number: string
  company_name: string
  hours: number
  is_closed: boolean
  locked_by_protocol: { id: string; number: string } | null
  /** Pracovník v tento den na této zakázce řídil. */
  drove?: boolean
  /** Poznámka k hodinám (Moje hodiny), kdo ji zapsal a kdy. */
  note?: string | null
  note_author?: string | null
  note_at?: string | null
}

/** Hodiny na interním úkolu v daném dni (jen mzdy a interní reporting). */
export interface PayrollDayTaskEntry {
  id: string
  task_id: string
  task_title: string
  hours: number
  note: string | null
  note_author?: string | null
  note_at?: string | null
  order_id: string | null
  order_number: string | null
  company_name: string | null
}

export interface PayrollDayAbsence {
  id: string
  type: string
  label: string
  color: string
  hours: string | number
  half_day: string | null
  status?: string
  /** Přílohy (neschopenka, potvrzení od lékaře…) — dostupné bez ohledu na stav nepřítomnosti. */
  attachments?: AbsenceAttachment[]
  note?: string | null
  note_author?: string | null
  note_at?: string | null
}

export interface PayrollWorker {
  id: string
  name: string
  initials: string | null
  color: string | null
  /** Zda je pracovník aktivní. Neaktivní se v přehledu zobrazují ztlumeně se štítkem. */
  active: boolean
  total_hours: number
  /** Z toho hodiny na interních úkolech (jsou v total_hours). */
  task_hours?: number
  worked_days: number
  /** Počet dnů, kdy pracovník řídil (víc zakázek v jeden den = jeden den). */
  driven_days: number
  absence_hours: number
  /** Hodiny náhradního volna – ve mzdách odpracováno, interně čerpání přesčasů. */
  comp_leave_hours?: number
  /**
   * Přesčasy v měsíci: bilance měsíce (odpracováno − fond; + nadpracováno / − chybí, běžící měsíc
   * k dnešku), připsáno do banky, proplaceno, čerpané náhradní volno, zůstatek banky ke konci měsíce.
   */
  overtime?: { fund: number; worked: number; balance: number; complete: boolean; paid_out: number; consumed: number; balance_end: number } | null
  daily: Record<string, {
    hours: number
    absences: PayrollDayAbsence[]
    entries: PayrollDayEntry[]
    task_entries?: PayrollDayTaskEntry[]
  }>
  orders: { id: string; number: string; company_name: string; hours: number }[]
  absences: { id?: string; date: string; label: string; color: string; half_day: string | null; attachments?: AbsenceAttachment[] }[]
  future_absences: { date: string; label: string; color: string; half_day: string | null }[]
}

export interface PayrollData {
  year: number
  month: number
  days_in_month: number
  holidays: Record<string, string>
  workers: PayrollWorker[]
}

export async function getPayrollReport(year: number, month: number): Promise<PayrollData> {
  const response = await client.get('/attendance/payroll', { params: { year, month } })
  return response.data.data
}

export interface OrderReportDayEntry {
  id: string
  user_id: string
  user_name: string
  user_initials: string | null
  user_color: string | null
  hours: number
  note: string | null
  locked_by_protocol: { id: string; number: string } | null
}

export interface OrderReportWorker {
  id: string
  name: string
  initials: string | null
  color: string | null
  hours: number
  /** Počet dnů, kdy pracovník na zakázce v měsíci zapsal hodiny. */
  days: number
  /** Km zapsané pracovníkem k zakázce v měsíci. */
  km: number
}

export interface OrderReportItem {
  id: string
  number: string
  company_name: string
  description: string | null
  is_closed: boolean
  total_hours: number
  /** Km všech pracovníků na zakázce v měsíci. */
  km: number
  worked_days: number
  worker_count: number
  workers: OrderReportWorker[]
  daily: Record<string, { hours: number; entries: OrderReportDayEntry[] }>
}

export interface OrderReportData {
  year: number
  month: number
  days_in_month: number
  holidays: Record<string, string>
  orders: OrderReportItem[]
}

export async function getOrderReport(year: number, month: number): Promise<OrderReportData> {
  const response = await client.get('/attendance/order-report', { params: { year, month } })
  return response.data.data
}

export interface CashVoucherExportResult {
  message: string
  document_number: string | null
  document_date: string
  orders_count: number
  total: number
  /** Počet záznamů práce označených tímto exportem. */
  marked_entries?: number
}

/** Doklad, který za měsíc z Flow do Pohody odešel. legacy = starší export (před evidencí), obsah neznámý. */
export interface PayrollExportDocument {
  export_id: string | null
  run_id: string | null
  document_number: string | null
  exported_at: string | null
  legacy: boolean
}

/**
 * Export mzdového reportu (pokladní doklad) do Pohody.
 * Když za měsíc doklad už odešel, server vrátí 409 s errors.code = 'already_exported' a seznamem dokladů;
 * znovu se pošle jen s confirmDeletedInPohoda = true (uživatel potvrdil, že doklad v Pohodě smazal).
 */
export async function exportCashVoucher(year: number, month: number, confirmDeletedInPohoda = false): Promise<CashVoucherExportResult> {
  const response = await client.post('/attendance/export-cash-voucher', { year, month, confirm_deleted_in_pohoda: confirmDeletedInPohoda })
  return response.data.data
}

export async function getPayrollExportStatus(year: number, month: number): Promise<{ year: number; month: number; documents: PayrollExportDocument[] }> {
  const response = await client.get('/attendance/payroll-export-status', { params: { year, month } })
  return response.data.data
}

/** Nastaví/zruší příznak „řídil jsem" na zakázku v daný den. */
export async function setOrderDriver(data: {
  order_id: string
  date: string
  drove: boolean
  user_id?: string | null
}): Promise<void> {
  await client.post('/attendance/driver', data)
}

export async function getMyWeek(
  year: number,
  week: number,
): Promise<{ year: number; week: number; week_dates: string[]; entries: TimeEntry[] }> {
  const response = await client.get('/attendance/my-week', { params: { year, week } })
  return response.data.data
}

export async function createAssignment(data: {
  order_id: string
  budget_hours?: string
  start_date: string
  end_date: string
  due_date?: string | null
  foreman_id?: string | null
  worker_ids: string[]
}): Promise<WorkAssignment> {
  const response = await client.post('/attendance/assignments', data)
  return response.data.data
}

export async function updateAssignment(
  id: string,
  data: {
    budget_hours?: string
    due_date?: string | null
    foreman_id?: string | null
  }
): Promise<WorkAssignment> {
  const response = await client.put(`/attendance/assignments/${id}`, data)
  return response.data.data
}

/**
 * Delete an assignment.
 *
 * Without `range`, removes the entire WorkAssignment (and all its day-planning rows /
 * time entries). With `range`, deletes only the day-planning rows + time entries that fall
 * inside [start_date, end_date]; the assignment itself is kept alive if it still has rows
 * outside that window. This prevents wiping January's plan when the user only wanted to
 * cancel February's planning of the same order.
 */
export async function deleteAssignment(
  id: string,
  range?: { start_date: string; end_date: string },
): Promise<void> {
  // axios.delete supports a body via the `data` option
  await client.delete(`/attendance/assignments/${id}`, range ? { data: range } : undefined)
}

/**
 * Existující přiřazení zakázky (jedna zakázka = jedno přiřazení). Modal si z něj předvyplní
 * rozpočet/parťáka a upozorní, že se plánují další dny do existujícího záznamu.
 */
export interface ExistingAssignmentLookup {
  id: string
  budget_hours: string
  due_date: string | null
  foreman: { id: string; name: string } | null
  worker_count: number
}

export async function lookupAssignment(orderId: string): Promise<ExistingAssignmentLookup | null> {
  const { data } = await client.get<{ success: boolean; data: { assignment: ExistingAssignmentLookup | null } }>(
    '/attendance/assignments/lookup',
    { params: { order_id: orderId } },
  )
  return data.data.assignment
}

/**
 * Add workers to an existing assignment for a given date range. Also generates day-level
 * planning rows (WorkAssignmentDay) for each worker × day in the range.
 */
export async function addWorkersToAssignment(
  id: string,
  data: { worker_ids: string[]; start_date: string; end_date: string },
): Promise<WorkAssignment> {
  const response = await client.post(`/attendance/assignments/${id}/add-workers`, data)
  return response.data.data
}

/**
 * Remove a single worker from an existing assignment. Deletes their day-planning rows but
 * preserves any time entries already recorded by them (those stay for audit/payroll).
 */
export async function removeWorkerFromAssignment(
  id: string,
  workerId: string,
): Promise<WorkAssignment> {
  const response = await client.post(`/attendance/assignments/${id}/remove-worker`, {
    worker_id: workerId,
  })
  return response.data.data
}

export async function createTimeEntry(data: {
  user_id?: string
  order_id: string
  date: string
  hours: number | string
  note?: string | null
}): Promise<TimeEntry> {
  const response = await client.post('/attendance/time-entries', data)
  return response.data.data
}

export async function bulkSaveTimeEntries(
  entries: {
    user_id: string
    order_id: string
    date: string
    hours: number | string
    note?: string | null
  }[]
): Promise<TimeEntry[]> {
  const response = await client.post('/attendance/time-entries/bulk', {
    entries,
  })
  return response.data.data
}

export async function deleteTimeEntry(id: string): Promise<void> {
  await client.delete(`/attendance/time-entries/${id}`)
}

export async function setDayWorkers(
  assignmentId: string,
  date: string,
  userIds: string[],
  note?: string | null,
  alertDays?: number | null,
): Promise<void> {
  await client.post('/attendance/day-workers', {
    assignment_id: assignmentId,
    date,
    user_ids: userIds,
    note: note ?? null,
    alert_days: alertDays ?? null,
  })
}

export interface WorkerReportData {
  year: number
  month: number | null
  total_hours: number
  weeks: {
    week_key: string
    year: number
    week: number
    date_range: string
    orders: {
      order: { id: string; number: string; company_name: string; description: string | null; is_closed: boolean }
      hours: number
      entries: { date: string; hours: string; note: string | null }[]
    }[]
    total_hours: number
  }[]
  absences: Absence[]
  absence_summary: {
    code: string
    label: string
    color: string
    count: number
  }[]
}

export async function getWorkerReport(year: number, month?: number, week?: number): Promise<WorkerReportData> {
  const params: any = { year }
  if (month) params.month = month
  if (week) params.week = week
  const response = await client.get('/attendance/my-report', { params })
  return response.data.data
}

export async function copyWeek(data: {
  assignment_id?: string
  source_year: number
  source_week: number
  target_year: number
  target_week: number
  copy_hours: boolean
}): Promise<{ message: string; copied_workers: number; copied_entries: number }> {
  const response = await client.post('/attendance/copy-week', data)
  return response.data.data
}

// --- Absences ---

export async function getAbsenceTypes(): Promise<AbsenceTypeConfig[]> {
  const response = await client.get('/attendance/absence-types')
  return response.data.data
}

export async function getHolidays(year: number): Promise<HolidayInfo[]> {
  const response = await client.get('/attendance/holidays', { params: { year } })
  return response.data.data
}

/**
 * Výsledek vytvoření nepřítomnosti. Jednodenní zadání vrací `id`, rozsah (od–do) vrací
 * `created_ids` (jeden záznam na každý pracovní den). Podle toho se navazují přílohy.
 */
export interface CreateAbsenceResult {
  id?: string
  created_ids?: string[]
  created_count?: number
  [key: string]: unknown
}

export async function createAbsence(data: {
  user_id?: string
  date: string
  end_date?: string
  absence_type_id: string
  hours?: number
  half_day?: HalfDay | null
  notify_user_ids?: string[]
  note?: string
}): Promise<CreateAbsenceResult> {
  const response = await client.post('/attendance/absences', data)
  return response.data.data
}

/** Úprava skutečné doby / poznámky (Lékař podle propustky). */
export async function updateAbsence(id: string, data: { hours?: number; note?: string }): Promise<void> {
  await client.put(`/attendance/absences/${id}`, data)
}

export async function deleteAbsence(id: string): Promise<void> {
  await client.delete(`/attendance/absences/${id}`)
}

// --- Přílohy nepřítomnosti (potvrzení od lékaře apod.) ---

export async function uploadAbsenceAttachment(
  absenceId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<AbsenceAttachment> {
  const formData = new FormData()
  formData.append('file', file)
  const r = await client.post(`/attendance/absences/${absenceId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && onProgress) {
        onProgress(Math.round((e.loaded * 100) / e.total))
      }
    },
  })
  return r.data.data
}

export async function deleteAbsenceAttachment(absenceId: string, attachmentId: string): Promise<void> {
  await client.delete(`/attendance/absences/${absenceId}/attachments/${attachmentId}`)
}

/**
 * Stáhne přílohu jako blob a spustí download v prohlížeči. Endpoint je chráněn JWT, takže
 * přímý `<a href>` nestačí — musíme přes axios klienta, který přidá Authorization hlavičku.
 */
export async function downloadAbsenceAttachment(
  absenceId: string,
  attachmentId: string,
  filename: string,
): Promise<void> {
  const r = await client.get(`/attendance/absences/${absenceId}/attachments/${attachmentId}`, { responseType: 'blob' })
  const url = URL.createObjectURL(r.data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Pro inline náhled obrázku — vrátí blob URL, kterou je nutné po unmount uvolnit. */
export async function fetchAbsenceAttachmentBlobUrl(absenceId: string, attachmentId: string): Promise<string> {
  const r = await client.get(`/attendance/absences/${absenceId}/attachments/${attachmentId}`, { responseType: 'blob' })
  return URL.createObjectURL(r.data)
}

export async function getMyAbsences(
  year: number,
  month: number,
): Promise<Absence[]> {
  const response = await client.get('/attendance/my-absences', { params: { year, month } })
  return response.data.data
}


// --- Notifikační příjemci nepřítomnosti ---

export async function getNotifyRecipients(): Promise<AbsenceNotifyRecipient[]> {
  const response = await client.get('/attendance/notify-recipients')
  return response.data.data
}

export async function createNotifyRecipient(
  user_id: string,
  is_permanent: boolean,
): Promise<AbsenceNotifyRecipient> {
  const response = await client.post('/attendance/notify-recipients', { user_id, is_permanent })
  return response.data.data
}

export async function updateNotifyRecipient(
  id: string,
  is_permanent: boolean,
): Promise<AbsenceNotifyRecipient> {
  const response = await client.put(`/attendance/notify-recipients/${id}`, { is_permanent })
  return response.data.data
}

export async function deleteNotifyRecipient(id: string): Promise<void> {
  await client.delete(`/attendance/notify-recipients/${id}`)
}

// --- Nepřítomnosti v rozsahu pro plánování ---

export async function getAbsencesRange(dateFrom: string, dateTo: string): Promise<PlanningAbsenceUser[]> {
  const response = await client.get('/attendance/absences-range', {
    params: { date_from: dateFrom, date_to: dateTo },
  })
  return response.data.data
}

// --- Schvalování nepřítomnosti ---

export interface AbsenceApprovalFilters {
  status?: string
  user_id?: string
  absence_type_id?: string
  date_from?: string
  date_to?: string
  page?: number
  per_page?: number
}

export interface Paginated<T> {
  items: T[]
  pagination: { total: number; page: number; per_page: number; total_pages: number }
}

export async function getAbsencesForApproval(
  filters: AbsenceApprovalFilters = {},
): Promise<Paginated<Absence>> {
  const params: Record<string, string | number> = {}
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params[k] = v as string | number
  })
  const response = await client.get('/attendance/absences-approval', { params })
  return response.data.data
}

export async function approveAbsence(id: string): Promise<Absence> {
  const response = await client.post(`/attendance/absences/${id}/approve`, {})
  return response.data.data
}

export async function rejectAbsence(id: string): Promise<Absence> {
  const response = await client.post(`/attendance/absences/${id}/reject`, {})
  return response.data.data
}

export type AbsenceStatusValue = 'pending' | 'approved' | 'rejected'

export async function setAbsenceStatus(id: string, status: AbsenceStatusValue): Promise<Absence> {
  const response = await client.post(`/attendance/absences/${id}/status`, { status })
  return response.data.data
}
